import { useEffect, useState, type FormEvent } from 'react';
import type { CampaignPurpose, CampaignRecord, SegmentFilter } from '@workspace/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { contactAgentApi } from '../api/contactAgentApi';
import { ContactAgentSegmentFields } from './ContactAgentSegmentFields';

type AudienceDialogProps = {
  campaign: CampaignRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (campaign: CampaignRecord) => void;
};

export function ContactAgentAudienceDialog({
  campaign,
  open,
  onOpenChange,
  onSaved,
}: AudienceDialogProps) {
  const [purpose, setPurpose] = useState<CampaignPurpose>('COBRANZAS');
  const [segmentFilter, setSegmentFilter] = useState<SegmentFilter>({ overdueOnly: true });
  const [segmentCount, setSegmentCount] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!campaign || !open) {
      return;
    }
    setPurpose(campaign.purpose);
    setSegmentFilter(campaign.segmentFilter ?? { overdueOnly: true });
    setSegmentCount(null);
    setError('');
  }, [campaign, open]);

  if (!campaign) {
    return null;
  }

  const preview = async () => {
    try {
      const result = await contactAgentApi.previewSegment(
        purpose === 'COBRANZAS' ? segmentFilter : {},
        purpose,
        campaign.agentId ?? undefined,
      );
      setSegmentCount(result.count);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo calcular el segmento');
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const updated = await contactAgentApi.updateCampaignAudience(campaign.id, {
        purpose,
          segmentFilter: purpose === 'COBRANZAS' ? segmentFilter : undefined,
      });
      onSaved(updated);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el publico');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Publico objetivo</DialogTitle>
          <DialogDescription>
            Cambia a quien se contacta de ahora en mas. Quienes ya fueron escritos, se dieron de
            baja o pagaron no se vuelven a contactar.
          </DialogDescription>
        </DialogHeader>
        <form className='space-y-4' onSubmit={handleSubmit}>
          <div className='space-y-2'>
            <Label htmlFor='audience-purpose'>Tipo</Label>
            <select
              id='audience-purpose'
              className='flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm'
              value={purpose}
              onChange={event => setPurpose(event.target.value as CampaignPurpose)}
            >
              <option value='COBRANZAS'>Cobranzas</option>
              <option value='AVISO'>Aviso / contacto</option>
            </select>
          </div>
          {purpose === 'COBRANZAS' ? (
            <ContactAgentSegmentFields value={segmentFilter} onChange={setSegmentFilter} />
          ) : (
            <p className='text-sm text-muted-foreground'>
              Aviso contacta la cartera cargada, sin filtrar por deuda.
            </p>
          )}
          <div className='flex items-center gap-3'>
            <Button type='button' variant='ghost' size='sm' onClick={() => void preview()}>
              Ver segmento
            </Button>
            {segmentCount !== null ? (
              <span className='text-sm text-muted-foreground'>{segmentCount} contactos</span>
            ) : null}
          </div>
          {error ? <p className='text-sm text-destructive'>{error}</p> : null}
          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type='submit' disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar publico'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
